import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ThumbsUp, ThumbsDown, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Was this helpful?" widget — rendered inline on closed tickets in the
 * client portal. Lets the owner record a thumbs-up / thumbs-down rating
 * + an optional comment.
 *
 * The component fetches existing feedback on mount so a returning visitor
 * sees their previous selection and can update it.
 *
 * Props:
 *   ticketId   string  required
 *   compact    bool    optional — render with tighter spacing for small slots
 */
const TicketFeedbackWidget = ({ ticketId, compact = false }) => {
  const [existing, setExisting] = useState(null);
  const [pendingRating, setPendingRating] = useState(null); // user clicked but hasn't submitted comment yet
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCommentBox, setShowCommentBox] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/tickets/${ticketId}/feedback`, { withCredentials: true });
        if (!cancelled && r.data) {
          setExisting(r.data);
          setComment(r.data.comment || '');
        }
      } catch {
        // 401/404 fine — widget will just show fresh state.
      }
    };
    load();
    return () => { cancelled = true; };
  }, [ticketId]);

  const submit = async (rating, withComment = false) => {
    setSubmitting(true);
    try {
      const body = { rating };
      if (withComment) body.comment = comment;
      const r = await axios.post(
        `${BACKEND_URL}/api/tickets/${ticketId}/feedback`,
        body,
        { withCredentials: true },
      );
      setExisting({ ...(existing || {}), ...r.data });
      toast.success('Thanks for the feedback');
      setShowCommentBox(false);
      setPendingRating(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClickRating = (rating) => {
    setPendingRating(rating);
    setShowCommentBox(true);
    submit(rating, false);  // record the rating immediately; comment is optional follow-up
  };

  const padCls = compact ? 'p-3' : 'p-4';
  const isHelpful = (existing?.rating || pendingRating) === 'helpful';
  const isNotHelpful = (existing?.rating || pendingRating) === 'not_helpful';

  return (
    <div className={`bg-muted/30 rounded-sm border border-border ${padCls}`} data-testid={`ticket-feedback-${ticketId}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          {existing ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
              Feedback recorded — thanks!
            </>
          ) : (
            'Was this support helpful?'
          )}
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={isHelpful ? 'default' : 'outline'}
            onClick={() => handleClickRating('helpful')}
            disabled={submitting}
            data-testid={`feedback-helpful-${ticketId}`}
          >
            <ThumbsUp className="w-3.5 h-3.5 mr-1.5" />
            Helpful
          </Button>
          <Button
            type="button"
            size="sm"
            variant={isNotHelpful ? 'destructive' : 'outline'}
            onClick={() => handleClickRating('not_helpful')}
            disabled={submitting}
            data-testid={`feedback-not-helpful-${ticketId}`}
          >
            <ThumbsDown className="w-3.5 h-3.5 mr-1.5" />
            Not Helpful
          </Button>
        </div>
      </div>

      {(showCommentBox || (existing && existing.comment)) && (
        <div className="mt-3 space-y-2">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional: tell us why (1000 chars max)"
            rows={2}
            maxLength={1000}
            className="bg-input text-sm"
            data-testid={`feedback-comment-${ticketId}`}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => submit(existing?.rating || pendingRating, true)}
            disabled={submitting || !(existing?.rating || pendingRating)}
            data-testid={`feedback-comment-submit-${ticketId}`}
          >
            {existing?.comment ? 'Update Comment' : 'Send Comment'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TicketFeedbackWidget;
